import { useState } from 'react';
import { Button, Spinner } from 'reactstrap';
import {
  useTranslator,
  useAppContext,
  useToast,
  getCountry,
  callNativeMobileApp,
  withMobileAppMode,
} from '@front-activerse/common-components';

import { compareVersions } from 'compare-versions';
import { useOutletContext } from 'react-router-dom';

// Parses Instacart API error bodies — single `{ error: { message } }` or
// multi `{ error: { errors: [{ error: { message }, meta: { key } }] } }`
// (see https://docs.instacart.com/developer_platform_api/errors).
const extractInstacartErrorMessage = (body) => {
  const err = body?.error;
  if (!err) return null;
  if (Array.isArray(err.errors) && err.errors.length > 0) {
    return err.errors
      .map((e) => {
        const key = e?.meta?.key;
        const msg = e?.error?.message;
        return key && msg ? `${key}: ${msg}` : msg;
      })
      .filter(Boolean)
      .join(', ');
  }
  return err.message || null;
};

const InstacartButton = ({
  meal,
  publicName,
  preparationImageUrl,
  mobileApp,
  multiplier = 1,
  description = 'Meal plan',
}) => {
  const {
    consts: { INSTACART_FIREBASE_FUNCTION, siteMetadata },
  } = useAppContext();

  const { t } = useTranslator();
  const { toastError } = useToast();
  const { trackFirebaseEvent } = useOutletContext();

  const [preloadInstacart, setPreloadInstacart] = useState(false);

  const handleInstaCart = async () => {
    const ingredients = [];
    const recipe = [];

    // Cookbook/MealPlan meals have nested `dishes`; UserMeals are flat.
    const dishes =
      Array.isArray(meal.dishes) && meal.dishes.length > 0
        ? meal.dishes
        : [{ ingredients: meal.ingredients || [], recipe: meal.recipe }];

    dishes.forEach((dish) => {
      dish.ingredients.forEach((ingredient) => {
        const measurement = ingredient.measurements[0];
        const unit = measurement.text.replace('%s', '').trim();

        ingredients.push({
          name: ingredient.name.toLowerCase(),
          display_text: ingredient.name,
          measurements: [
            {
              quantity: measurement.amount * multiplier,
              unit,
            },
          ],
        });
      });

      if (dish.recipe) {
        dish.recipe.split(/\r?\n/).forEach((line) => {
          const step = line.replace(/^\s*\d+\.\s*/, '').trim();
          if (step) recipe.push(step);
        });
      }
    });

    let mealImg = '';

    if (preparationImageUrl) {
      mealImg = preparationImageUrl;
    }

    const request = {
      name: publicName,
      ingredients,
      recipe,
      image: mealImg,
      description,
      author: siteMetadata.title,
      servings: meal.portions * multiplier,
      landing_page_configuration: {
        partner_linkback_url: window.location.href,
        enable_pantry_items: true,
      },
    };

    if (meal.preparationTime?.minutes) {
      request.cooking_time = meal.preparationTime.minutes;
    }

    setPreloadInstacart(true);

    try {
      const response = await fetch(INSTACART_FIREBASE_FUNCTION, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      const data = await response.json();

      if (!response.ok) {
        const message = extractInstacartErrorMessage(data);
        console.error('Instacart API error:', response.status, data);
        toastError(message ? <p className="mb-0">{message}</p> : undefined);
        return;
      }

      if (!data.instacartUrl) {
        console.error('Instacart response missing instacartUrl:', data);
        toastError();
        return;
      }

      const instacartUrl = data.instacartUrl;

      trackFirebaseEvent('user_action', {
        action: 'tap-instacart_button',
        location: 'Meal_details',
      });

      if (
        mobileApp &&
        mobileApp.os &&
        mobileApp.os.toLowerCase().startsWith('ios') &&
        mobileApp.version &&
        compareVersions(mobileApp.version, '4.0.2')
      ) {
        callNativeMobileApp({
          action: 'deeplink_open',
          payload: {
            url: instacartUrl,
          },
        });
      } else {
        window.location = instacartUrl;
      }
    } catch (err) {
      console.error('Instacart fetch error:', err);
      toastError();
    } finally {
      setPreloadInstacart(false);
    }
  };

  if (getCountry() === 'Canada' || getCountry() === 'United States of America') {
    return (
      <div className="text-center">
        <Button
          color="instacart"
          className="w-100 w-md-auto"
          onClick={handleInstaCart}
          disabled={preloadInstacart}
        >
          {preloadInstacart && (
            <Spinner color="primary" size="sm" className="me-2">
              {t('loading.message')}
            </Spinner>
          )}
          {t('orderIngredients.instacart.button')}
        </Button>
      </div>
    );
  }

  return null;
};

export default withMobileAppMode(InstacartButton);
